import { HotelFilterState, HotelSortKey, HotelViewMode } from '@/src/types/hotels/srp'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface HotelSrpState {
    sort: HotelSortKey
    filters: HotelFilterState
    /** SRP list vs map view; RN parity map toggle. */
    viewMode: HotelViewMode
    /** Hotel whose map pin/list card is highlighted; null = none. */
    selectedHotelId: string | null
}

const INITIAL_FILTERS: HotelFilterState = {
    query: '',
    priceMin: null,
    priceMax: null,
    stars: [],
    guestRatingMin: null,
    hotDealOnly: false,
    amenities: [],
    propertyTypes: [],
    districts: []
}

const initialState: HotelSrpState = {
    sort: 'recommended',
    filters: INITIAL_FILTERS,
    viewMode: 'list',
    selectedHotelId: null
}

const toggle = <T>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value]

// SRP sort + filter selections. Kept in the store rather than in the page container so
// the filter rail, sort bar and bottom bar can each read what they need directly
// instead of the container drilling one hook return through every level.
const hotelSrpSlice = createSlice({
    name: 'hotelSrp',
    initialState,
    reducers: {
        setHotelSort: (state, action: PayloadAction<HotelSortKey>) => {
            state.sort = action.payload
        },
        setHotelQuery: (state, action: PayloadAction<string>) => {
            state.filters.query = action.payload
        },
        setHotelPriceRange: (state, action: PayloadAction<{ min: number | null; max: number | null }>) => {
            state.filters.priceMin = action.payload.min
            state.filters.priceMax = action.payload.max
        },
        setHotelGuestRatingMin: (state, action: PayloadAction<number | null>) => {
            state.filters.guestRatingMin = action.payload
        },
        toggleHotelHotDeal: state => {
            state.filters.hotDealOnly = !state.filters.hotDealOnly
        },
        toggleHotelStar: (state, action: PayloadAction<number>) => {
            state.filters.stars = toggle(state.filters.stars, action.payload)
        },
        toggleHotelAmenity: (state, action: PayloadAction<string>) => {
            state.filters.amenities = toggle(state.filters.amenities, action.payload)
        },
        toggleHotelPropertyType: (state, action: PayloadAction<string>) => {
            state.filters.propertyTypes = toggle(state.filters.propertyTypes, action.payload)
        },
        toggleHotelDistrict: (state, action: PayloadAction<string>) => {
            state.filters.districts = toggle(state.filters.districts, action.payload)
        },
        clearHotelFilters: state => {
            state.filters = INITIAL_FILTERS
        },
        setHotelViewMode: (state, action: PayloadAction<HotelViewMode>) => {
            state.viewMode = action.payload
            // Leaving the map drops the highlighted pin so re-opening starts clean.
            if (action.payload === 'list') state.selectedHotelId = null
        },
        setSelectedHotel: (state, action: PayloadAction<string | null>) => {
            state.selectedHotelId = action.payload
        },
        // A new search invalidates every facet selection — districts and property types
        // from the previous city would filter the new results down to nothing.
        resetHotelSrp: () => initialState
    }
})

export const {
    setHotelSort,
    setHotelQuery,
    setHotelPriceRange,
    setHotelGuestRatingMin,
    toggleHotelHotDeal,
    toggleHotelStar,
    toggleHotelAmenity,
    toggleHotelPropertyType,
    toggleHotelDistrict,
    clearHotelFilters,
    setHotelViewMode,
    setSelectedHotel,
    resetHotelSrp
} = hotelSrpSlice.actions

export default hotelSrpSlice.reducer
